import { useChatDocumentViewer } from '@/hooks/useChatDocumentViewer';
import { cn } from '@/lib/utils';
import type { ChatDocumentQuoteExcerptBlockPayload } from '@/types/chatBlocks';
import { splitSourceTitle } from './mcgrawHillCitations';
import { mcgrawHillReferenceById } from './mcgrawHillReferences';

/** Where a quote's passages are joined ("first … second"). */
const PASSAGE_BREAK = /\s*…\s*/;

/**
 * The header's source line: an article as journal · year · title, a book
 * chapter as book · chapter.
 */
export const quoteSourceParts = (
  title: string | null | undefined
): { name: string; details: string[] } => {
  const { book, rest } = splitSourceTitle(title ?? undefined);
  const detail = rest.replace(/^,\s*/, '');
  const dated = /^(.*\S)\s+(\d{4})$/.exec(book);

  return dated
    ? { name: dated[1], details: [dated[2], detail].filter(Boolean) }
    : { name: book, details: detail ? [detail] : [] };
};

/**
 * mcgraw-hill: a passage quoted from a reference document inside an answer:
 * the source, the passage (joined passages kept apart by an ellipsis), and a
 * link that opens the document at that page with the passage highlighted.
 */
const McGrawHillQuoteCard = ({
  payload,
}: {
  payload: ChatDocumentQuoteExcerptBlockPayload;
}) => {
  const { open } = useChatDocumentViewer();

  const isArticle =
    mcgrawHillReferenceById(payload.documentId)?.role === 'article';

  const { name, details } = quoteSourceParts(payload.title);
  const passages = payload.excerpt.split(PASSAGE_BREAK).filter(Boolean);

  return (
    <figure className="mcgraw-hill-quote">
      <div className="mcgraw-hill-figure__header">
        <span
          className={cn(
            'mcgraw-hill-sources__mark',
            isArticle && 'mcgraw-hill-sources__mark--article'
          )}
          aria-hidden="true"
        />
        <span className="mcgraw-hill-figure__title">
          <strong>{name}</strong>
          {details.map((detail) => ` · ${detail}`).join('')}
        </span>
        <button
          type="button"
          className="mcgraw-hill-figure__open"
          onClick={() =>
            open(
              { kind: 'chatDocument', documentId: payload.documentId },
              { page: payload.page, highlightText: passages[0] }
            )
          }
        >
          {isArticle ? 'Full paper' : 'Open chapter'}
        </button>
      </div>
      <blockquote className="mcgraw-hill-quote__body">
        {passages.map((passage, index) => (
          <span key={index}>
            {index > 0 ? (
              <span className="mcgraw-hill-quote__break" aria-label="omitted">
                {' … '}
              </span>
            ) : null}
            {passage}
          </span>
        ))}
      </blockquote>
    </figure>
  );
};

export default McGrawHillQuoteCard;
